/**
 * The Speedy player's hls.js loader (F-129; A3-ALIGN §7): a saved piece or playlist comes from the
 * segment store, everything else goes to the network through hls.js's own loader, unchanged.
 *
 * - Pieces are matched by address path (the pass in the query differs every time); a ranged
 *   request is never served from the store.
 * - A saved playlist is served for its exact address (the saved master's pass is under 90 minutes
 *   old, so its piece addresses still work for the pieces that weren't saved).
 * - A piece served from memory reports a load time that matches the 240p rate, so hls.js's
 *   bandwidth estimate doesn't jump to a quality that wasn't saved.
 * - Answers are given asynchronously and never after `abort()`/`destroy()`.
 */
import Hls, { type HlsConfig, type Loader, type LoaderCallbacks, type LoaderConfiguration, type LoaderContext, type LoaderStats } from 'hls.js';
import { segmentStore } from './segment-store';

/** The 240p rate the reported load time is based on (avg ≈ 450 kbps, measured). */
const SAVED_RATE_BPS = 450_000;

const Base = Hls.DefaultConfig.loader as unknown as new (config: HlsConfig) => Loader<LoaderContext>;

export function servedFromStore(context: LoaderContext): string | ArrayBuffer | null {
  if (context.rangeStart !== undefined || context.rangeEnd !== undefined) return null;
  if (context.responseType === 'arraybuffer') return segmentStore.piece(context.url);
  return segmentStore.playlistFor(context.url);
}

export class SpeedyLoader extends Base {
  private live = true;
  private timer: ReturnType<typeof setTimeout> | null = null;

  load(context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>): void {
    const saved = servedFromStore(context);
    if (saved === null) {
      super.load(context, config, callbacks);
      return;
    }
    this.context = context;
    const size = typeof saved === 'string' ? saved.length : saved.byteLength;
    const now = performance.now();
    const ms = typeof saved === 'string' ? 1 : Math.max(1, (size * 8 * 1000) / SAVED_RATE_BPS);
    const stats = this.stats as LoaderStats;
    stats.loading.start = now - ms;
    stats.loading.first = now - ms / 2;
    stats.loading.end = now;
    stats.loaded = size;
    stats.total = size;
    stats.chunkCount = 1;
    // A copy for arraybuffers: hls.js transfers buffers to its worker, which would empty the store.
    const data = typeof saved === 'string' ? saved : saved.slice(0);
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.live) return;
      callbacks.onSuccess({ url: context.url, data }, stats, context, null);
    }, 0);
  }

  abort(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      (this.stats as LoaderStats).aborted = true;
    }
    super.abort();
  }

  destroy(): void {
    this.live = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    super.destroy();
  }
}
